import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SleepCalendar from '../components/SleepCalendar';
import StreakMilestones from '../components/StreakMilestones';
import EarnedRewards from '../components/EarnedRewards';

const { FiArrowLeft, FiCheck, FiX, FiRotateCcw, FiSun, FiMoon, FiBarChart3, FiTrendingUp } = FiIcons;

const LoggingModal = ({ isOpen, onClose, onSubmit, selectedGoodValue, child }) => {
  const [note, setNote] = useState('');

  const handleSubmit = (skipNote = false) => {
    onSubmit(skipNote ? '' : note.trim());
    setNote('');
  };

  const handleClose = () => {
    setNote('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-center p-4 z-50 overflow-y-auto"
        style={{ alignItems: 'flex-start', paddingTop: '2rem' }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: -20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: -20 }}
          className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl my-4"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-800">Add Sleep Log</h3>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="text-gray-500" />
            </motion.button>
          </div>

          {/* Child Info */}
          <div className="text-center mb-6">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="text-4xl mb-2"
            >
              {child?.avatarEmoji}
            </motion.div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">{child?.name}</h4>
            <p className="text-gray-600">
              You're logging a{' '}
              <strong className={selectedGoodValue ? 'text-green-600' : 'text-red-600'}>
                {selectedGoodValue ? 'Good Night' : 'Not Tonight'}
              </strong>
              {' '}for today.
            </p>
          </div>

          {/* Status Indicator */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`p-4 rounded-xl mb-6 text-center ${
              selectedGoodValue 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <div className={`text-3xl mb-2 ${selectedGoodValue ? 'text-green-600' : 'text-red-600'}`}>
              {selectedGoodValue ? '🌙✨' : '😴💤'}
            </div>
            <p className={`font-semibold ${selectedGoodValue ? 'text-green-700' : 'text-red-700'}`}>
              {selectedGoodValue ? 'Great job on a good bedtime!' : 'No worries, tomorrow is a new night!'}
            </p>
          </motion.div>

          {/* Note Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add a note about bedtime (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 140))}
              placeholder="e.g., Fell asleep quickly after story time..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200"
              rows="3"
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">{note.length}/140 characters</p>
              {note.length > 100 && (
                <p className="text-xs text-amber-600">Getting close to limit!</p>
              )}
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex space-x-3"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSubmit(true)}
              className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Skip Note
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSubmit(false)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-colors font-medium text-white ${
                selectedGoodValue 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              <SafeIcon icon={FiCheck} />
              <span>Save Log</span>
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const ChildSleepPage = () => {
  const { childId } = useParams();
  const { 
    children, 
    sleep_logs, 
    addSleepLog, 
    getChildStats,
    earned_rewards,
    rewards,
    markRewardAsDelivered,
    removeSleepLog
  } = useApp();
  
  const [showRewardBanner, setShowRewardBanner] = useState(null);
  const [showLoggingModal, setShowLoggingModal] = useState(false);
  const [selectedGoodValue, setSelectedGoodValue] = useState(null);

  const child = children.find(c => c.id === parseInt(childId));
  const todayDate = format(new Date(), 'yyyy-MM-dd');
  const todayLog = sleep_logs.find(log => 
    log.childId === parseInt(childId) && log.date === todayDate
  );

  const stats = getChildStats(parseInt(childId));

  // Check for newly earned rewards
  const newlyEarnedRewards = earned_rewards.filter(
    er => er.childId === parseInt(childId) && !er.deliveredAt
  );

  if (!child) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-700">Child not found</h2>
        <Link to="/" className="text-purple-600 hover:underline mt-2 inline-block">
          Return to Dashboard
        </Link>
      </div>
    );
  }

  const handleSleepLog = (good) => {
    setSelectedGoodValue(good);
    setShowLoggingModal(true);
  };

  const handleLoggingSubmit = (note) => {
    const previousEarnedCount = earned_rewards.filter(
      er => er.childId === parseInt(childId)
    ).length;

    addSleepLog({
      childId: parseInt(childId),
      date: todayDate,
      good: selectedGoodValue,
      note: note
    });

    // Check if new rewards were earned
    setTimeout(() => {
      const newEarnedCount = earned_rewards.filter(
        er => er.childId === parseInt(childId)
      ).length;
      
      if (newEarnedCount > previousEarnedCount) {
        const latestReward = earned_rewards
          .filter(er => er.childId === parseInt(childId))
          .sort((a, b) => new Date(b.earnedOn) - new Date(a.earnedOn))[0];
        
        if (latestReward) {
          const reward = rewards.find(r => r.id === latestReward.rewardId);
          if (reward) {
            setShowRewardBanner({
              rewardName: reward.name,
              earnedRewardId: latestReward.id
            });
          }
        }
      }
    }, 100);

    setShowLoggingModal(false);
    setSelectedGoodValue(null);
  };

  const handleLoggingClose = () => {
    setShowLoggingModal(false);
    setSelectedGoodValue(null);
  };

  const handleUndoLog = () => {
    if (todayLog) {
      removeSleepLog(parseInt(childId), todayDate);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center space-x-4"
      >
        <Link
          to="/"
          className="p-2 hover:bg-white rounded-lg transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl text-gray-600" />
        </Link>
        <div className="flex items-center space-x-3">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="text-2xl"
          >
            {child.avatarEmoji}
          </motion.div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{child.name}</h1>
            <p className="text-gray-500">Age {child.age}</p>
          </div>
        </div>
      </motion.div>

      {/* Reward Banner */}
      <AnimatePresence>
        {showRewardBanner && (
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.95 }}
            className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-4 rounded-xl shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <motion.span
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 0.5, repeat: 3 }}
                  className="text-2xl"
                >
                  🎁
                </motion.span>
                <div>
                  <h3 className="font-semibold">Reward Unlocked!</h3>
                  <p>{showRewardBanner.rewardName}</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  markRewardAsDelivered(showRewardBanner.earnedRewardId);
                  setShowRewardBanner(null);
                }}
                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
              >
                Mark as Delivered
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Today's Logging Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6">How was bedtime tonight?</h2>
        
        <div className="flex justify-center space-x-4 mb-6">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSleepLog(true)}
            className="flex items-center space-x-3 bg-green-600 text-white px-8 py-4 rounded-2xl hover:bg-green-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
          >
            <SafeIcon icon={FiSun} className="text-xl" />
            <span>Good Night</span>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSleepLog(false)}
            className="flex items-center space-x-3 bg-white text-red-600 border-2 border-red-600 px-8 py-4 rounded-2xl hover:bg-red-50 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
          >
            <SafeIcon icon={FiMoon} className="text-xl" />
            <span>Not Tonight</span>
          </motion.button>
        </div>

        {/* Today's Log Status and Undo Button */}
        <AnimatePresence>
          {todayLog && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="bg-gray-50 p-4 rounded-xl border-l-4 border-purple-500">
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  Today's result: {todayLog.good ? '✅ Good Night' : '❌ Not Tonight'}
                </p>
                {todayLog.note && (
                  <p className="text-gray-600">
                    <strong>Note:</strong> {todayLog.note}
                  </p>
                )}
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleUndoLog}
                className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg bg-gray-500 text-white hover:bg-gray-600 transition-colors font-medium"
              >
                <SafeIcon icon={FiRotateCcw} />
                <span>Undo Today's Log</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Calendar */}
      <SleepCalendar 
        childId={parseInt(childId)} 
        showTodayLogging={false}
        todayLog={todayLog}
        onUndoLog={handleUndoLog}
      />

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Stats</h2>
        
        <div className="grid grid-cols-3 gap-4">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="text-center p-4 bg-purple-50 rounded-xl"
          >
            <div className="text-2xl mb-1">🔥</div>
            <div className="text-2xl font-bold text-purple-600">{stats.currentStreak}</div>
            <div className="text-sm text-gray-500">Current Streak</div>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="text-center p-4 bg-orange-50 rounded-xl"
          >
            <div className="text-2xl mb-1">🔥</div>
            <div className="text-2xl font-bold text-orange-600">{stats.bestStreak}</div>
            <div className="text-sm text-gray-500">Best Streak</div>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="text-center p-4 bg-blue-50 rounded-xl"
          >
            <div className="text-2xl mb-1">🌙</div>
            <div className="text-2xl font-bold text-blue-600">{stats.totalGoodNights}</div>
            <div className="text-sm text-gray-500">Total Good Nights</div>
          </motion.div>
        </div>
      </motion.div>

      {/* Streak Milestones */}
      <StreakMilestones childId={parseInt(childId)} />

      {/* Earned Rewards */}
      <EarnedRewards childId={parseInt(childId)} />

      {/* Analytics Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Analytics</h2>
            <p className="text-gray-600">View detailed sleep patterns and behavior insights</p>
          </div>
          <div className="text-4xl">📊</div>
        </div>

        <Link
          to={`/child/${childId}/analytics`}
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
        >
          <SafeIcon icon={FiTrendingUp} className="text-lg" />
          <span>View Analytics Dashboard</span>
          <SafeIcon icon={FiTrendingUp} className="text-lg" />
        </Link>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
          <div className="flex items-center space-x-2 text-green-600">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Weekly patterns</span>
          </div>
          <div className="flex items-center space-x-2 text-blue-600">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>Monthly trends</span>
          </div>
          <div className="flex items-center space-x-2 text-purple-600">
            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
            <span>Best performing days</span>
          </div>
          <div className="flex items-center space-x-2 text-orange-600">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span>Improvement suggestions</span>
          </div>
        </div>
      </motion.div>

      {/* Logging Modal */}
      <LoggingModal
        isOpen={showLoggingModal}
        onClose={handleLoggingClose}
        onSubmit={handleLoggingSubmit}
        selectedGoodValue={selectedGoodValue}
        child={child}
      />
    </div>
  );
};

export default ChildSleepPage;